import { useParams, Link } from 'react-router-dom';
import { articles, type Article } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article: Article | undefined = articles.find((a) => a.id === id);

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h1>
          <Link to="/" className="text-sm text-primary-500 hover:text-primary-600">返回首页</Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    image: article.imageUrl,
    publisher: {
      '@type': 'Organization',
      name: '快猫平台',
    },
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - 快猫平台`}
        description={article.excerpt}
        keywords={`快猫,${article.tags.join(',')},${category?.name || ''}`}
        canonicalUrl={`https://kuaimao.com/article/${article.id}`}
        ogType="article"
        schemaJson={articleSchema}
      />
      <main className="min-h-screen bg-background-50 pt-16">
        <div className="max-w-4xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: category?.name || '分类', href: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <article className="px-4 md:px-6 py-8 md:py-12">
            <div className="mb-8">
              <div className="flex flex-wrap gap-2 mb-4">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground-950 mb-4 leading-snug">
                {article.title}
              </h1>

              <div className="flex items-center gap-4 text-sm text-foreground-400">
                <span>{article.author}</span>
                <span>·</span>
                <span>{article.date}</span>
                <span>·</span>
                <span>{article.readTime}</span>
              </div>
            </div>

            <div className="aspect-[16/9] rounded-lg overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>

            <div
              className="prose-custom text-sm md:text-base text-foreground-700 leading-relaxed space-y-4"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/60">
              <div className="flex items-center gap-2 mb-6">
                <span className="text-sm font-medium text-foreground-600">分享文章：</span>
                <button className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-8 h-8 flex items-center justify-center rounded-md bg-background-100 text-foreground-500 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <div className="px-4 md:px-6 pb-16">
              <h3 className="text-lg font-heading font-bold text-foreground-950 mb-5">
                相关文章
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.id}`}
                    className="group rounded-lg bg-background-100 border border-background-200/60 hover:border-primary-200 overflow-hidden transition-all duration-300 cursor-pointer"
                  >
                    <div className="aspect-[16/9] overflow-hidden">
                      <img
                        src={ra.imageUrl}
                        alt={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="text-xs font-semibold text-foreground-950 mb-1 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {ra.title}
                      </h4>
                      <span className="text-[10px] text-foreground-400">{ra.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}